import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useIntl } from "react-intl";
import { MenuItem } from "@/components/design/MenuItem";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { ShareIcon } from "@/components/share/ShareItemCard";
import { useStartChat } from "@/components/chat/use-start-chat";
import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { useMe } from "@/auth/use-me";
import {
  CompanyIdentitiesDocument,
  type CompanyIdentitiesQuery,
  GlobalSearchDocument,
  MyPinGroupsDocument,
  PinGroupMembershipsDocument,
  PinType,
  SearchEntityType,
  SearchPersonsDocument,
  type SearchPersonsQuery,
  SharedItemsDocument,
  type SharedItemsQuery,
} from "@/graphql/generated/operations";
import { usePin } from "@/pages/save/use-pin";

type SearchPersonsPerson = SearchPersonsQuery["persons"]["nodes"][number];
type SharedItemNode = SharedItemsQuery["sharedItems"]["nodes"][number];
type SearchImage = NonNullable<SharedItemNode["mainImage"]>;
type CompanyLogo = CompanyIdentitiesQuery["companyIdentities"]["companies"][number]["logo"];

// Topbar inline search: start typing → dropdown with hits (Teamplayer /
// Projekte / Posts). No separate results page. Ported node-by-node from
// workshop/ui/SearchBar.jsx.
//
// Engineering swap: workshop's three custom hooks (useSearchPersons +
// useSearchSharedItems(PROJECT) + useSearchSharedItems(POST)) become three
// inline useQuery() calls against tech-stack's existing SearchPersons +
// SharedItems documents, each with the appropriate where clause.

function tooShort(term: string): boolean {
  return !term || term.trim().length < 2;
}

// How many rows each bucket shows.
const BUCKET_SIZE = 6;

// ONE QUERY PER BUCKET, NOT ONE QUERY SLICED INTO BUCKETS.
//
// globalSearch ranks across entity types together, so a single shared budget is spent by whichever
// type happens to rank highest. With four buckets and one `take: 18` that was not a risk but a
// routine outcome: a term matching twenty people filled every slot with people, and the user saw an
// empty "Companies" group for a term that genuinely matches a company. Raising the budget to 24 only
// moves that threshold - it does not remove it, because ranking still decides who spends it.
// Fetching each type under its own budget is what makes starvation impossible rather than unlikely.
//
// The cost is four indexed full-text queries instead of one, behind a 250ms debounce.


// PERSON overfetches; the other three do not.
//
// genesis writes one `global_search` row PER INDEXED ATTRIBUTE for a person (name, profession,
// education, …) but a single row for a company, a project and a post. So six PERSON rows can be six
// fragments of one person, and taking six rows would show one person in a six-row bucket. Overfetch,
// then de-duplicate by person id down to BUCKET_SIZE.
//
// This is a ceiling, not a guarantee: a person with more than PERSON_OVERFETCH indexed attributes
// can still crowd the bucket. Removing the ceiling entirely needs a distinct-on-person in genesis,
// which is a schema change and not this ticket.
const PERSON_OVERFETCH = 24;

/** One bucket's worth of ranked rows for a single entity type. */
function useBucket(entityType: SearchEntityType, term: string, skip: boolean, take: number) {
  return useQuery(GlobalSearchDocument, {
    variables: { input: { query: term, entityTypes: [entityType], take } as LooseInput },
    skip,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseInput = any;

export function SearchBar() {
  const navigate = useNavigate();
  const intl = useIntl();
  const me = useMe();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);

  const goExtended = () => {
    const term = q.trim();
    setOpen(false);
    navigate(term ? `/extended-search?q=${encodeURIComponent(term)}` : "/extended-search");
  };

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const term = debounced.trim();
  const skip = tooShort(term);

  // One globalSearch call per bucket, each with its own budget. Spelled out rather than mapped over
  // an array so they are plainly four unconditional hook calls in a fixed order.
  const personSearch = useBucket(SearchEntityType.Person, term, skip, PERSON_OVERFETCH);
  const companySearch = useBucket(SearchEntityType.Company, term, skip, BUCKET_SIZE);
  const projectSearch = useBucket(SearchEntityType.SharedItemProject, term, skip, BUCKET_SIZE);
  const postSearch = useBucket(SearchEntityType.SharedItemPost, term, skip, BUCKET_SIZE);

  const personIds = useMemo(() => {
    // De-duplicate: several rows of one person must not each claim a slot.
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const r of personSearch.data?.globalSearch.results ?? []) {
      if (!r.personId || seen.has(r.personId)) continue;
      seen.add(r.personId);
      ids.push(r.personId);
      if (ids.length === BUCKET_SIZE) break;
    }
    return ids;
  }, [personSearch.data]);

  const projectIds = useMemo(
    () => (projectSearch.data?.globalSearch.results ?? []).map((r) => r.entityId).slice(0, BUCKET_SIZE),
    [projectSearch.data],
  );
  // The index holds an id and a label, never an image (same reasoning as useGlobalCompanySearch /
  // genesis#263), so the logo needs its own hydration round-trip - companyIdentitiesQ below.
  const companies = useMemo(
    () => (companySearch.data?.globalSearch.results ?? []).slice(0, BUCKET_SIZE),
    [companySearch.data],
  );
  const postIds = useMemo(
    () => (postSearch.data?.globalSearch.results ?? []).map((r) => r.entityId).slice(0, BUCKET_SIZE),
    [postSearch.data],
  );

  const personsQ = useQuery(SearchPersonsDocument, {
    variables: { input: { where: { id: { in: personIds } }, first: personIds.length } as LooseInput },
    skip: skip || personIds.length === 0,
  });
  const sharedIds = useMemo(() => [...projectIds, ...postIds], [projectIds, postIds]);
  const sharedQ = useQuery(SharedItemsDocument, {
    variables: { input: { where: { id: { in: sharedIds } }, first: sharedIds.length } as LooseInput },
    skip: skip || sharedIds.length === 0,
  });
  const companyIds = useMemo(() => companies.map((c) => c.entityId), [companies]);
  // BUCKET_SIZE (6) is well under genesis's MAX_COMPANY_IDENTITIES cap, so unlike
  // useGlobalCompanySearch this never needs to split the ask across rounds.
  const companyIdentitiesQ = useQuery(CompanyIdentitiesDocument, {
    variables: { input: { ids: companyIds } as LooseInput },
    skip: skip || companyIds.length === 0,
  });

  const personById = useMemo(() => {
    const map = new Map<string, SearchPersonsPerson>();
    for (const n of personsQ.data?.persons?.nodes ?? []) map.set(n.id, n);
    return map;
  }, [personsQ.data]);
  const sharedById = useMemo(() => {
    const map = new Map<string, SharedItemNode>();
    for (const n of sharedQ.data?.sharedItems?.nodes ?? []) map.set(n.id, n);
    return map;
  }, [sharedQ.data]);
  const companyLogoById = useMemo(() => {
    const map = new Map<string, CompanyLogo>();
    for (const c of companyIdentitiesQ.data?.companyIdentities.companies ?? []) map.set(c.id, c.logo ?? null);
    return map;
  }, [companyIdentitiesQ.data]);

  // Preserve globalSearch rank order within each bucket.
  const personNodes = personIds
    .map((id) => personById.get(id))
    .filter((n): n is SearchPersonsPerson => Boolean(n));
  const projectNodes = projectIds
    .map((id) => sharedById.get(id))
    .filter((n): n is SharedItemNode => Boolean(n));
  const postNodes = postIds
    .map((id) => sharedById.get(id))
    .filter((n): n is SharedItemNode => Boolean(n));
  const loading =
    personSearch.loading ||
    companySearch.loading ||
    projectSearch.loading ||
    postSearch.loading ||
    personsQ.loading ||
    sharedQ.loading;
  // Companies count. They were missing here, so a term matching ONLY a company rendered the Companies
  // bucket underneath the words "No matches" — `total` is the same switch the empty state reads, so
  // omitting a bucket from it tells the user there is nothing to find while showing them something.
  const total =
    personNodes.length + companies.length + projectNodes.length + postNodes.length;
  const showDropdown = open && q.trim().length >= 2;

  const personLabel = (p: SearchPersonsPerson): string => {
    const given = (p.name.given ?? []).filter(Boolean).join(" ");
    return [given, p.name.family].filter(Boolean).join(" ") || "–";
  };

  return (
    <div className="relative w-full" data-graphql="GlobalSearch, SearchPersons, SharedItems, CompanyIdentities">
      <input
        type="text"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={intl.formatMessage({
          id: "search.placeholder",
          defaultMessage: "Search team players, projects, posts …",
        })}
        className="h-9 w-full rounded-full bg-transparent pl-4 pr-16 text-sm text-ink ring-1 ring-ink/15 placeholder:text-ink/45 transition focus:outline-none focus:ring-ink/30"
      />
      {q ? (
        <Button
          variant="icon"
          size="sm"
          title={intl.formatMessage({ id: "search.clear", defaultMessage: "Clear search" })}
          aria-label={intl.formatMessage({ id: "search.clear", defaultMessage: "Clear search" })}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setQ("");
            setOpen(true);
          }}
          className="absolute right-9 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-ink/45 hover:bg-ink/[0.06] hover:text-ink"
        >
          <span className="block -translate-y-px text-[17px] leading-none">×</span> {/* i18n-allow */}
        </Button>
      ) : null}
      <Button
        variant="icon"
        size="sm"
        title={intl.formatMessage({ id: "search.extended", defaultMessage: "Extended search" })}
        aria-label={intl.formatMessage({ id: "search.extended", defaultMessage: "Extended search" })}
        onMouseDown={(e) => e.preventDefault()}
        onClick={goExtended}
        className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-[15px] leading-none text-ink/45 hover:text-accent"
      >
        ↗
      </Button>
      {showDropdown ? (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-96 overflow-auto rounded-md bg-surface shadow-lg ring-1 ring-ink/10">
          {loading && total === 0 ? (
            <div className="px-4 py-3 text-[12px] text-ink/45">
              {intl.formatMessage({ id: "search.searching", defaultMessage: "Searching …" })}
            </div>
          ) : total === 0 ? (
            <div className="px-4 py-3 text-[12px] text-ink/45">
              {intl.formatMessage({ id: "search.noResults", defaultMessage: "No matches" })}
            </div>
          ) : (
            <>
              <SearchGroup
                title={intl.formatMessage({
                  id: "search.groupTeamplayers",
                  defaultMessage: "Team players",
                })}
                items={personNodes}
                render={(p) => (
                  <SearchRow
                    key={p.id}
                    title={personLabel(p)}
                    sub={p.profession ?? null}
                    avatar={p.avatar ?? null}
                    onSelect={() => {
                      setOpen(false);
                      navigate(`/p/${p.id}`);
                    }}
                    actions={
                      // No chat/pin on your own entry: you can't message or pin yourself.
                      me.id && p.id === me.id ? undefined : (
                        <PersonRowActions
                          personId={p.id}
                          initialPinned={Boolean(p.isPinned)}
                          onMessaged={() => setOpen(false)}
                        />
                      )
                    }
                  />
                )}
              />
              <SearchGroup
                title={intl.formatMessage({
                  id: "search.groupCompanies",
                  defaultMessage: "Companies",
                })}
                items={companies}
                render={(c) => (
                  <SearchRow
                    key={c.entityId}
                    title={c.label ?? "–"}
                    sub={null}
                    avatar={companyLogoById.get(c.entityId) ?? null}
                    onSelect={() => {
                      setOpen(false);
                      navigate(`/c/${c.entityId}`);
                    }}
                  />
                )}
              />
              <SearchGroup
                title={intl.formatMessage({
                  id: "search.groupProjects",
                  defaultMessage: "Projects",
                })}
                items={projectNodes}
                render={(p) => (
                  <SearchRow
                    key={p.id}
                    title={p.name ?? "–"}
                    sub={p.shortDescription ?? null}
                    image={p.mainImage ?? null}
                    onSelect={() => {
                      setOpen(false);
                      navigate(`/share/discover/${p.id}`);
                    }}
                  />
                )}
              />
              <SearchGroup
                title={intl.formatMessage({ id: "search.groupPosts", defaultMessage: "Posts" })}
                items={postNodes}
                render={(p) => (
                  <SearchRow
                    key={p.id}
                    title={p.name ?? "–"}
                    sub={p.shortDescription ?? null}
                    image={p.mainImage ?? null}
                    onSelect={() => {
                      setOpen(false);
                      navigate(`/share/discover/${p.id}`);
                    }}
                  />
                )}
              />
            </>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={goExtended}
            className="flex w-full items-center gap-2 border-t border-ink/5 px-4 py-2.5 text-left text-[12px] text-accent hover:bg-ink/[0.06]"
          >
            <span className="text-[14px] leading-none">↗</span>
            {intl.formatMessage({ id: "search.useExtended", defaultMessage: "Use extended search" })}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function SearchGroup<T>({
  title,
  items,
  render,
}: {
  title: string;
  items: readonly T[];
  render: (item: T) => ReactNode;
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border-b border-ink/5 last:border-0">
      <div className="px-4 pb-1 pt-2.5 text-[9px] tracking-[0.25em] text-ink/40">
        {title} · {items.length}
      </div>
      {items.map(render)}
    </div>
  );
}

function SearchRow({
  title,
  sub,
  avatar,
  image,
  onSelect,
  actions,
}: {
  title: string;
  sub: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  avatar?: any;
  image?: SearchImage | null;
  onSelect?: () => void;
  actions?: ReactNode;
}) {
  const avatarNode =
    avatar !== undefined ? (
      <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full ring-1 ring-ink/5">
        {avatar?.content ? (
          <img
            src={`data:${avatar.mimeType || "image/jpeg"};base64,${avatar.content}`}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center font-serif text-[11px]"
            style={AVATAR_PLACEHOLDER_STYLE}
          >
            {avatarInitials(title)}
          </div>
        )}
      </div>
    ) : null;
  const imageNode =
    image !== undefined ? (
      <div className="h-10 w-14 shrink-0 overflow-hidden rounded bg-ink/[0.04] ring-1 ring-ink/5">
        {image?.content ? (
          <img
            src={`data:${image.mimeType || "image/jpeg"};base64,${image.content}`}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
    ) : null;
  const textNode = (
    <div className="min-w-0 flex-1">
      <div className="truncate text-[13px] text-ink/80">{title || "–"}</div>
      {sub ? <div className="truncate text-[11px] text-ink/45">{sub}</div> : null}
    </div>
  );
  return (
    <div className="group flex items-center gap-3 px-4 py-2 hover:bg-ink/[0.06]">
      {onSelect ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
        >
          {avatarNode}
          {imageNode}
          {textNode}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {avatarNode}
          {imageNode}
          {textNode}
        </div>
      )}
      {actions ? (
        <div className="shrink-0 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

// Quick actions on a person search hit: pin (with optional group picker) and
// start/open a chat. mousedown is suppressed on the wrapper so the input's blur
// doesn't close the dropdown before the click lands.
function PersonRowActions({
  personId,
  initialPinned,
  onMessaged,
}: {
  personId: string;
  initialPinned: boolean;
  onMessaged: () => void;
}) {
  const intl = useIntl();
  const { hasChat, busy, startChat } = useStartChat(personId);

  async function message(e: MouseEvent) {
    e.stopPropagation();
    await startChat();
    onMessaged();
  }

  return (
    <div className="flex items-center gap-1" onMouseDown={(e) => e.preventDefault()}>
      <PinGroupPicker personId={personId} initialPinned={initialPinned} />
      <Button
        variant="icon"
        size="sm"
        onClick={message}
        disabled={busy}
        title={
          hasChat
            ? intl.formatMessage({ id: "search.openChat", defaultMessage: "Open chat" })
            : intl.formatMessage({ id: "search.writeMessage", defaultMessage: "Write a message" })
        }
        aria-label={
          hasChat
            ? intl.formatMessage({ id: "search.openChat", defaultMessage: "Open chat" })
            : intl.formatMessage({ id: "search.writeMessage", defaultMessage: "Write a message" })
        }
        className={hasChat ? "text-accent" : ""}
      >
        {busy ? <Spinner /> : <ShareIcon name="comment" filled={hasChat} className="h-4 w-4" />}
      </Button>
    </div>
  );
}

// Pin icon that opens a popover: pin/unpin (no group) plus, optionally, pick a
// pin group to drop the person into. A person pin lives in at most one group
// (single-select, removePinsFromOtherGroups). Groups + memberships load lazily
// when the popover opens.
function PinGroupPicker({
  personId,
  initialPinned,
}: {
  personId: string;
  initialPinned: boolean;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(initialPinned);
  const [pendingId, setPendingId] = useState<string | null>(null); // group id or "base"
  const [confirmingGroupId, setConfirmingGroupId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Anchor (fixed coords) for the portaled popover, so it escapes the search
  // dropdown's overflow clipping instead of being cut off inside the box.
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const pin = usePin();

  function toggleOpen() {
    if (open) {
      setOpen(false);
      setConfirmingGroupId(null);
      return;
    }
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setAnchor({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  }

  const groupsQ = useQuery(MyPinGroupsDocument, {
    variables: { input: { pinTypes: [PinType.Person] } },
    skip: !open,
  });
  const memQ = useQuery(PinGroupMembershipsDocument, {
    variables: { input: { personId } },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const groups =
    groupsQ.data?.pinGroups.__typename === "PinGroupResult" ? groupsQ.data.pinGroups.pinGroups : [];
  const memberIds = new Set<string>(
    memQ.data?.pinGroupMemberships?.__typename === "PinGroupMembershipResult"
      ? memQ.data.pinGroupMemberships.pinGroups.map((g) => g.id)
      : [],
  );

  async function run(key: string, fn: () => Promise<unknown>, nextPinned?: boolean) {
    if (pendingId) return;
    setPendingId(key);
    try {
      await fn();
      if (nextPinned !== undefined) setPinned(nextPinned);
      await memQ.refetch();
      setConfirmingGroupId(null);
    } finally {
      setPendingId(null);
    }
  }

  const pinNoGroup = () => run("base", () => pin.add({ personIds: [personId] }), true);
  const unpin = () => run("base", () => pin.remove({ personIds: [personId] }), false);
  const pinInto = (gid: string) =>
    run(
      gid,
      () =>
        pin.add({
          personIds: [personId],
          groups: [gid],
          removePinsFromOtherGroups: true,
        }),
      true,
    );
  const removeFromGroup = (gid: string) =>
    run(gid, () =>
      pin.add({
        personIds: [personId],
        groups: [...memberIds].filter((x) => x !== gid),
        removePinsFromOtherGroups: true,
      }),
    );

  return (
    // stopPropagation so clicking the icon doesn't trigger the row's
    // open-profile navigation.
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        title={intl.formatMessage({ id: "search.pin", defaultMessage: "Pin" })}
        aria-label={intl.formatMessage({ id: "search.pin", defaultMessage: "Pin" })}
        aria-pressed={pinned}
        className={buttonClasses({
          variant: "icon",
          size: "sm",
          className: pinned ? "text-accent" : "",
        })}
      >
        <ShareIcon name="pin" filled={pinned} className="h-4 w-4" />
      </button>
      {open && anchor
        ? createPortal(
            <>
              {/* Keep mousedown from blurring the search input (which would close
                  the whole dropdown and unmount this popover). */}
              <div
                className="fixed inset-0 z-[60]"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setOpen(false);
                  setConfirmingGroupId(null);
                }}
              />
              <div
                className="fixed z-[61] w-56 rounded-md bg-surface p-2 shadow-lg ring-1 ring-ink/10"
                style={{ top: anchor.top, right: anchor.right }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
              >
                {groups.length > 0 ? (
                  <>
                    <div className="flex flex-wrap gap-1 px-1 pb-1 pt-0.5">
                      {groups.map((g) => {
                        const isMember = memberIds.has(g.id);
                        const pending = pendingId === g.id;
                        const confirming = confirmingGroupId === g.id;
                        return (
                          <button
                            key={g.id}
                            type="button"
                            disabled={pendingId !== null}
                            onClick={() => {
                              if (!isMember) {
                                setConfirmingGroupId(null);
                                void pinInto(g.id);
                                return;
                              }
                              if (confirming) void removeFromGroup(g.id);
                              else setConfirmingGroupId(g.id);
                            }}
                            title={
                              isMember
                                ? intl.formatMessage(
                                    { id: "search.removeFromGroup", defaultMessage: "Remove from {name}" },
                                    { name: g.name },
                                  )
                                : intl.formatMessage(
                                    { id: "search.pinInto", defaultMessage: "Pin into {name}" },
                                    { name: g.name },
                                  )
                            }
                            aria-label={
                              isMember
                                ? intl.formatMessage(
                                    { id: "search.removeFromGroup", defaultMessage: "Remove from {name}" },
                                    { name: g.name },
                                  )
                                : intl.formatMessage(
                                    { id: "search.pinInto", defaultMessage: "Pin into {name}" },
                                    { name: g.name },
                                  )
                            }
                            className={buttonClasses({
                              variant: confirming ? "destructive" : isMember ? "chip" : "ghost",
                              size: "sm",
                              className: "gap-1 disabled:opacity-60",
                            })}
                          >
                            {pending ? <Spinner className="h-3 w-3 text-ink/40" /> : isMember && !confirming ? "✓ " : null}
                            {confirming
                              ? intl.formatMessage({
                                  id: "search.confirmRemoveFromGroup",
                                  defaultMessage: "Remove?",
                                })
                              : g.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="my-1 border-t border-ink/5" />
                  </>
                ) : null}
                <MenuItem
                  disabled={pendingId !== null}
                  onClick={() => void (pinned ? unpin() : pinNoGroup())}
                  icon={pendingId === "base" ? <Spinner className="h-3.5 w-3.5 text-ink/40" /> : undefined}
                >
                  {pinned
                    ? intl.formatMessage({ id: "search.unpin", defaultMessage: "Unpin" })
                    : intl.formatMessage({ id: "search.pinNoGroup", defaultMessage: "Pin (no group)" })}
                </MenuItem>
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}

function Spinner({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
      aria-hidden
    />
  );
}
